import {Box, Button, Grid, IconButton, Stack, Typography} from '@mui/material';
import {useObservable} from '@ngneat/react-rxjs';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Outlet, useNavigate} from 'react-router-dom';
import {finalize} from 'rxjs';

import {wasteFamiliesQuery, wasteFamiliesService} from '@store/settings/wasteFamilies';

import {GroupAction} from '@icons/index';

import InfosCard from '@components/cards/infos/Infos.card';
import TableCard from '@components/cards/table/Table.card';
import AlertComponent from '@components/modal/Alert.component';

const WasteTab = () => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();

  const [deleteLoading, setDeleteLoading] = useState(false);

  const [showDeleteFamily, setShowDeleteFamily] = useState<number | false>(false);
  const [showDeleteWaste, setShowDeleteWaste] = useState<number | false>(false);

  const [{wasteFamilies}] = useObservable(wasteFamiliesQuery.wasteFamilies$);
  const [{allWaste}] = useObservable(wasteFamiliesQuery.allWastes$);

  useEffect(() => {
    wasteFamiliesService.getWasteFamilies().subscribe({
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
    wasteFamiliesService.getAllWaste().subscribe({
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  }, [enqueueSnackbar]);

  return (
    <>
      <Stack flex={1} width="100%" spacing={2} p="25px">
        <Typography fontSize="30px" fontWeight="700">
          {t('routes.waste')}
        </Typography>
        <Stack spacing={2}>
          {wasteFamilies.map((wasteFamily) => (
            <TableCard
              key={wasteFamily.id}
              icon={(
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-glass" width="22"
                     height="22" viewBox="0 0 24 24" strokeWidth="2" stroke="#29BD52" fill="none"
                     strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="15" x2="12" y2="21"/>
                  <path d="M17 3l1 7c0 3.012 -2.686 5 -6 5s-6 -1.988 -6 -5l1 -7h10z"/>
                </svg>
              )}
              menuOptions={(
                <>
                  <IconButton
                    onClick={(evt) => {
                      evt.stopPropagation();
                      navigate(`${wasteFamily.id}`);
                    }}>
                    <GroupAction style={{height: 20, width: 20}}/>
                  </IconButton>
                </>
              )}
              title={wasteFamily.name}>
              <Stack py="20px" spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography fontSize={18} fontWeight="700">{t('settings.waste.subtitle')}</Typography>
                </Stack>
                <Box>
                  <Grid container spacing={2} columns={6}>
                    {allWaste?.filter((waste) => waste.wasteFamilyId === wasteFamily.id)
                      .map((waste) => (
                        <Grid item key={waste.id} xs={1}>
                          <InfosCard
                            title={waste.name}
                            value={waste.code}
                            menuOptions={(
                              <>
                                <IconButton
                                  sx={{mr: '-7px'}}
                                  onClick={(evt) => {
                                    evt.stopPropagation();
                                    navigate(`${wasteFamily.id}/${waste.id}`);
                                  }}>
                                  <GroupAction style={{height: 14, width: 14}}/>
                                </IconButton>
                              </>
                            )}
                            unit={t('global.kmPerM3', {value: waste.density})}/>
                        </Grid>
                      ))}
                  </Grid>
                </Box>
              </Stack>
            </TableCard>
          ))}
        </Stack>
      </Stack>
      <Outlet/>
      {showDeleteFamily && (
        <AlertComponent
          title={t('delete.title')}
          description={t('delete.description.wasteFamily')}
          handleClose={() => setShowDeleteFamily(false)}
          actions={(
            <>
              <Button
                variant="outlined"
                color="primary"
                disabled={deleteLoading}
                onClick={() => setShowDeleteFamily(false)}>
                {t('global.cancel')}
              </Button>
              <Button
                variant="contained"
                disabled={deleteLoading}
                color="primary"
                onClick={() => {
                  setDeleteLoading(true);
                  wasteFamiliesService.deleteWasteFamily(showDeleteFamily).pipe(finalize(() => setDeleteLoading(false))).subscribe({
                    next: () => {
                      enqueueSnackbar(t('delete.success.wasteFamily'), {variant: 'success'});
                      setShowDeleteFamily(false);
                    },
                    error: (err) => enqueueSnackbar(err.text, err.options),
                  });
                }}>
                {t('global.delete')}
              </Button>
            </>
          )}/>
      )}
      {showDeleteWaste && (
        <AlertComponent
          title={t('delete.title')}
          description={t('delete.description.waste')}
          handleClose={() => setShowDeleteWaste(false)}
          actions={(
            <>
              <Button
                variant="outlined"
                color="primary"
                disabled={deleteLoading}
                onClick={() => setShowDeleteWaste(false)}>
                {t('global.cancel')}
              </Button>
              <Button
                variant="contained"
                disabled={deleteLoading}
                color="primary"
                onClick={() => {
                  setDeleteLoading(true);
                  wasteFamiliesService.deleteWaste(showDeleteWaste).pipe(finalize(() => setDeleteLoading(false))).subscribe({
                    next: () => {
                      enqueueSnackbar(t('delete.success.waste'), {variant: 'success'});
                      setShowDeleteWaste(false);
                    },
                    error: (err) => enqueueSnackbar(err.text, err.options),
                  });
                }}>
                {t('global.delete')}
              </Button>
            </>
          )}/>
      )}
    </>
  );
};

export default WasteTab;
